/* eslint-disable max-len */

import { useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { Button, Center, Column, CustomSpinner, Icon, Row, Text } from '~/commonComponents';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import { useGetProfessionalCountsByOffice } from '~/domain/Professionals/usecases/useGetProfessionalCountsByOffice';
import { useProfessionOptions } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { firePermPostJobJobDescriptionSubmitted } from '~/posthogs/events';
import { Colors } from '~/themes/colors';
import { pluralize } from '~/utils/String';
import {
  AutoSaveStatus,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import { JobDescriptionForm } from '../JobDetailsForm/components/JobDescription';
import { formatProfessionalCount } from '../utils';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import {
  useAutoSaveDraftJobPost,
  useAutoScrollToSection,
  useDraftJobPost,
  useSaveJobPost,
} from './hooks';

export const JobDescriptionPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const { draft, isLoading } = useDraftJobPost(id);
  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  const jobDescriptionRef = useRef<HTMLDivElement>(null);

  const controller = useJobDetailsForm({
    initialValues: draft,
  });

  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  useAutoScrollToSection({
    sectionRefs: {
      [CreatePermanentJobPostingFormSection.JobDescription]: jobDescriptionRef,
    },
    isReady: !isLoading && !!draft,
  });

  const { isPending, hasFormErrors, onSave } = useSaveJobPost(controller, id, {
    targetStep: CreatePermanentJobPostingSteps.ReviewAndPayment,
    triggers: ['jobDescription'],
    onTrackSuccess: (postingId, formData) => {
      firePermPostJobJobDescriptionSubmitted({
        postingId,
        professionType: formData.professionType,
      });
    },
  });
  const {
    isLoading: isAutoSaveLoading,
    isShowSavedMessage,
    lastSavedAt,
  } = useAutoSaveDraftJobPost(controller, id);

  const user = useUserSelector();
  const {
    countsByProfession,
    isLoading: isCountLoading,
    isError: isCountError,
  } = useGetProfessionalCountsByOffice(user?.id, {
    enable: isEnforcePostAJobFunnelImprovement,
    distance: 25,
  });

  const { findByCode } = useProfessionOptions();
  const professionTypeValue = controller.watch('professionType');
  const professionTypeLabel = findByCode(professionTypeValue)?.label ?? '';

  const totalCount = countsByProfession
    ? (countsByProfession[professionTypeValue as keyof typeof countsByProfession] ?? 0)
    : 0;

  const showCount =
    isEnforcePostAJobFunnelImprovement && !isCountLoading && !isCountError && totalCount > 0;

  if (isLoading) {
    return (
      <Center marginBlock={48}>
        <CustomSpinner isLoading size={24} />
      </Center>
    );
  }

  if (isEnforcePostAJobFunnelImprovement) {
    return (
      <>
        <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
        <Column alignItems="center" gap={16}>
          <Column alignItems="center" gap={10}>
            <Text bold fontSize={32} color={Colors.neutral_600} textAlign="center">
              One More Step to Receive Applicants
            </Text>
            <Text fontSize={16} color={Colors.neutral_500} textAlign="center">
              {showCount
                ? `Add your job description and your role goes out to ${formatProfessionalCount(totalCount)} ${pluralize(professionTypeLabel.toLowerCase(), totalCount)}.`
                : `Add your job description and your role goes out to ${professionTypeLabel.toLowerCase()} in your area.`}
            </Text>
          </Column>
          {hasFormErrors && (
            <Row gap={6} alignItems="center" width={800}>
              <Icon name="alert-circle" />
              <Text color={Colors.error_500}>
                Oops! Job description must be at least 150 characters.
              </Text>
            </Row>
          )}
          <FormProvider {...controller}>
            <Column width={800} alignSelf="center">
              <InfoSection
                ref={jobDescriptionRef}
                title="Job Description"
                titleFontSize={24}
                titleColor="neutral_600"
              >
                <JobDescriptionForm
                  control={controller.control}
                  errors={controller.formState.errors}
                />
              </InfoSection>
            </Column>
          </FormProvider>
          <Row justifyContent="center" marginTop={24}>
            <Button text="Last Step" onClick={onSave} isLoading={isPending || isAutoSaveLoading} />
          </Row>
        </Column>
      </>
    );
  }

  return (
    <>
      <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
      <Column width="60%" alignSelf="center">
        <Text bold fontSize={22} marginBottom={14} marginLeft={24}>
          Last Step: Job Description
        </Text>
        {hasFormErrors && <GeneralErrorMessage />}
        <FormProvider {...controller}>
          <Column gap={16}>
            <InfoSection
              icon="document-solid"
              ref={jobDescriptionRef}
              required
              title="Job Description"
            >
              <JobDescriptionForm
                control={controller.control}
                errors={controller.formState.errors}
              />
            </InfoSection>
          </Column>
        </FormProvider>
        <Row justifyContent="center" marginTop={24}>
          <Button text="Next" onClick={onSave} isLoading={isPending || isAutoSaveLoading} />
        </Row>
      </Column>
    </>
  );
};
